import { Grid, IconButton, MenuItem } from '@mui/material';
import ClearableSelect from 'components/ClearableSelect/ClearableSelect';
import usePatientFieldsMap from 'hooks/usePatientFieldsMap';
import { produce } from 'immer';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { filterState } from 'state/exportState';
import { set } from 'object-path';
import { Close } from '@mui/icons-material';
import {
  Condition,
  ConditionOperation,
  FieldType,
} from 'api/models/FieldTemplate';
import ConditionOperationSelect from 'components/GlobalDialog/AddFieldTemplate/components/ConditionConfig/ConditionOperationSelect';
import ConditionFieldRenderer from 'components/GlobalDialog/AddFieldTemplate/components/ConditionConfig/ConditionFieldRenderer';
import { getConditionValueType } from 'utils';

interface PatientFilterRowProps {
  availablePatientFields?: string[];
  index: number;
}

export default function PatientFilterRow({
  availablePatientFields = [],
  index,
}: PatientFilterRowProps) {
  const { t } = useTranslation();
  const [filters, setFilters] = useRecoilState(filterState);
  const patientFieldMap = usePatientFieldsMap();
  const patientFields =
    ['idTaj', 'idOther', 'patientProjectId', ...availablePatientFields]?.map(
      (patientField: string) => {
        if (!patientFieldMap.has(patientField)) {
          throw new Error(
            `Patient field ${patientField} not found in patientFieldMap, probably new fields were added in the API`,
          );
        }

        return patientFieldMap.get(patientField)!;
      },
    ) ?? [];

  const filter = filters[index] as Condition;

  const valueType = getConditionValueType(
    patientFieldMap.get(filter.reference?.attribute!),
  );

  return (
    <Grid container spacing={1}>
      <Grid item xs={3}>
        <ClearableSelect
          label={t('export.patientData')}
          required
          fullWidth
          value={filter.reference.attribute ?? ''}
          onChange={event => {
            const updatedFilters = produce(filters, draft => {
              set(draft[index], 'reference.attribute', event.target.value);
              set(draft[index], 'value', null);
              const type = patientFieldMap.get(event.target.value)?.fieldType;
              if (type === FieldType.Select || type === FieldType.StringList) {
                set(draft[index], 'operation', ConditionOperation.IS_ANY_OF);
              } else {
                set(draft[index], 'operation', ConditionOperation.EQUALS);
              }
            });
            setFilters(updatedFilters);
          }}
          error={!filter.reference.attribute}
          helperText={
            filter.reference.attribute ? '' : t('general.errors.mandatoryField')
          }
        >
          {patientFields.map(patientField => (
            <MenuItem key={patientField.id} value={patientField.id}>
              {patientField.fieldAttributes.label}
            </MenuItem>
          ))}
        </ClearableSelect>
      </Grid>
      <Grid item xs={1}>
        <ConditionOperationSelect
          operation={filter.operation}
          onChange={operation => {
            const updatedFilters = produce(filters, draft => {
              set(draft[index], 'operation', operation);
            });
            setFilters(updatedFilters);
          }}
          type={valueType}
        />
      </Grid>
      <Grid item xs={7.5}>
        <ConditionFieldRenderer
          patientFieldAttribute={filter?.reference.attribute}
          alias={filter?.reference.alias}
          value={filter?.value}
          onChange={v => {
            let value = v;

            if (
              (Array.isArray(v) &&
                (v.length === 0 || (v.length === 1 && !v[0]))) ||
              !value
            ) {
              value = null;
            }

            const updatedFilters = produce(filters, draft => {
              set(draft[index], 'value', value);
            });
            setFilters(updatedFilters);
          }}
        />
      </Grid>
      <Grid item xs={0.5}>
        <IconButton
          sx={{ mt: 2 }}
          size="small"
          onClick={() => {
            const updatedFilters = produce(filters, draft => {
              draft.splice(index, 1);
            });
            setFilters(updatedFilters);
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Grid>
    </Grid>
  );
}
